import { useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import {
  Conditional,
  MyEquipmentSearchForm,
  MyEquipmentTab,
  MyEquipmentTabPanel,
  useBreakpoint
} from 'components/pcc-components';
import { getProductName } from '@cat-ecom/common/utils/stringUtils';
import { UseEquipmentSuggestionsResult } from 'components/my-equipment/types';
import { ModelNumberHelpDrawer } from '../model-number-help-drawer/model-number-help-drawer';
import { EquipmentSuggestionsResults } from 'components/my-equipment/add-equipment/add-equipment-form/equipment-suggestions-results';
import { useEquipmentSearchSuggestions } from 'components/my-equipment/hooks/use-equipment-search-suggestions';
import {
  ADD_EQUIPMENT_BY_MODEL_ERRORS,
  FORM_FIELD_MODEL,
  EQUIPMENT_SEARCH_FILTER,
  SELECTED_EQUIPMENT,
  MEQ_MODEL_NO_MATCH,
  FAILED_TO_FETCH,
  DUPLICATE_EQUIPMENT_ERROR
} from 'components/my-equipment/constants';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { AddEquipmentDuplicateBanner } from '../../add-equipment-duplicate-banner';
import { useFeedback } from 'providers/feedback-provider';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import {
  ADD_EQUIPMENT_BY_MODEL,
  STATUS_FAIL,
  STATUS_SUCCESS
} from 'constants/analyticsConstants';
import { AddEquipmentProps } from '../types';

export const AddEquipmentByModel = ({
  setShowSearchSuggestionFailureBanner,
  handleYesClickedDuplicateBanner,
  showBackButton,
  isChild,
  formLocation
}: Readonly<AddEquipmentProps>) => {
  const t = useTranslations();
  const { fireEvent } = useFormSubmitEvent();
  const isSmallViewport = useBreakpoint(PAGE_BREAKPOINTS.BLOCKS_SM + 1);
  const {
    clearErrors,
    getValues,
    resetField,
    setError,
    setValue,
    watch,
    reset
  } = useFormContext();
  const {
    clearSuggestions,
    getEquipmentSuggestions,
    error,
    isError,
    isFetching,
    genericError,
    setGenericError,
    status,
    suggestions
  }: UseEquipmentSuggestionsResult = useEquipmentSearchSuggestions();
  const watchModelInput = watch(FORM_FIELD_MODEL);
  const validSearchTermLength = getValues(FORM_FIELD_MODEL).length >= 2;
  const hideDropdown = !validSearchTermLength && isSmallViewport;
  const { dismissFeedback, showFeedback, feedbackMessage } = useFeedback();
  const showDuplicateEquipmentBanner =
    showFeedback && feedbackMessage === DUPLICATE_EQUIPMENT_ERROR;
  const formLocationText = formLocation || t('ADD_EQUIPMENT_DRAWER_HEADING');

  const onEquipmentSelected = (value) => {
    value.serialNumber = '';
    setValue(
      FORM_FIELD_MODEL,
      `${value?.model} ${getProductName(value?.equipmentFamily)}`
    );
    setValue(SELECTED_EQUIPMENT, value);
    clearErrors();
  };

  const handleSearch = (value) => {
    dismissFeedback();
    setGenericError(false);
    const shouldSearchEquipments = value.length >= 2;
    clearSuggestions();

    if (shouldSearchEquipments) {
      getEquipmentSuggestions({
        searchTerm: value,
        filter: EQUIPMENT_SEARCH_FILTER.MODEL
      });
    }
  };

  const showSearchSuggestionFailureBanner = useCallback(
    (value) => {
      setShowSearchSuggestionFailureBanner &&
        setShowSearchSuggestionFailureBanner(value);
    },
    [setShowSearchSuggestionFailureBanner]
  );

  const handleNoClickedDuplicateBanner = () => {
    dismissFeedback();
    clearSuggestions();
    reset();
  };

  useEffect(() => {
    if (!watchModelInput) {
      resetField(FORM_FIELD_MODEL);
      setValue(SELECTED_EQUIPMENT, null);
      clearErrors();
      showSearchSuggestionFailureBanner(false);
      dismissFeedback();
    }
  }, [
    clearErrors,
    dismissFeedback,
    resetField,
    setValue,
    showSearchSuggestionFailureBanner,
    watchModelInput
  ]);

  useEffect(() => {
    if (!isError) {
      if (suggestions?.equipments?.length >= 1) {
        fireEvent({
          formName: ADD_EQUIPMENT_BY_MODEL,
          formStatus: STATUS_SUCCESS,
          formContent: getValues(FORM_FIELD_MODEL),
          formLocation: formLocationText
        });
      }
      showSearchSuggestionFailureBanner(false);
      return;
    }
    if (error?.message === MEQ_MODEL_NO_MATCH) {
      fireEvent({
        formName: ADD_EQUIPMENT_BY_MODEL,
        formStatus: STATUS_FAIL,
        formContent: getValues(FORM_FIELD_MODEL),
        formLocation: formLocationText,
        formFieldCausingError: t('MEQ_MODEL_NO_MATCH')
      });
      setError(FORM_FIELD_MODEL, {
        type: ADD_EQUIPMENT_BY_MODEL_ERRORS.MODEL_NOT_FOUND
      });
      showSearchSuggestionFailureBanner(false);
      return;
    }
    if (error.message) {
      fireEvent({
        formName: ADD_EQUIPMENT_BY_MODEL,
        formStatus: STATUS_FAIL,
        formContent: getValues(FORM_FIELD_MODEL),
        formLocation: formLocationText
      });
      if (error.message === FAILED_TO_FETCH) {
        showSearchSuggestionFailureBanner(true);
      } else {
        showSearchSuggestionFailureBanner(false);
        setError(FORM_FIELD_MODEL, {
          type: ADD_EQUIPMENT_BY_MODEL_ERRORS.DEFAULT_ERROR
        });
      }
    }
  }, [
    error,
    isError,
    setError,
    status,
    showSearchSuggestionFailureBanner,
    dismissFeedback,
    suggestions,
    watchModelInput,
    fireEvent,
    getValues,
    formLocationText,
    t
  ]);

  useEffect(() => {
    if (!genericError) return;
    fireEvent({
      formName: ADD_EQUIPMENT_BY_MODEL,
      formStatus: STATUS_FAIL,
      formContent: getValues(FORM_FIELD_MODEL),
      formLocation: formLocationText
    });
    clearSuggestions();
    clearErrors();
  }, [
    clearErrors,
    clearSuggestions,
    fireEvent,
    formLocationText,
    genericError,
    getValues
  ]);

  return (
    <>
      <MyEquipmentTab tabProps={{ ariaLabelledBy: t('BY_MODEL') }}>
        {t('BY_MODEL')}
      </MyEquipmentTab>
      <MyEquipmentTabPanel>
        <>
          <MyEquipmentSearchForm
            showDuplicateEquipmentError={showDuplicateEquipmentBanner}
            isEmpty={hideDropdown}
            name={FORM_FIELD_MODEL}
            onSearch={handleSearch}
            placeholder={t('ADD_EQUIPMENT_MODEL_PLACEHOLDER')}
            dataCsMask
          >
            <Conditional test={validSearchTermLength}>
              <EquipmentSuggestionsResults
                error={error}
                fetchStatus={status}
                isSmallViewport={isSmallViewport}
                isFetching={isFetching}
                onEquipmentSelected={onEquipmentSelected}
                searchTerm={getValues(FORM_FIELD_MODEL)}
                suggestions={suggestions}
              />
            </Conditional>
          </MyEquipmentSearchForm>
          <Conditional test={showDuplicateEquipmentBanner}>
            <AddEquipmentDuplicateBanner
              onAccept={handleYesClickedDuplicateBanner}
              onCancel={handleNoClickedDuplicateBanner}
            />
          </Conditional>
          <ModelNumberHelpDrawer
            showBackButton={showBackButton}
            isChild={isChild}
          />
        </>
      </MyEquipmentTabPanel>
    </>
  );
};
